import { Injectable, inject } from '@angular/core'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Observable, finalize, take } from 'rxjs'
import { TokenizedMessageLoadingService } from '../tokenized-message-loading/tokenized-message-loading.service'
import { KeyingAppEnvironmentConfig } from '../../configuration/keying.app.environment.config'

/**
 * Structure interface - represents a structure entity
 */
export interface IStructure {
  city: string
  stateCode: string
  countryCode: string
  groupId: number
  keywayFamilyId: number
  combinatingRuleSetId: number
  chamberTypeId: number
  hasConstructionKeys: boolean
  hasDocuments: boolean
  id: number
  structureId: string
  name: string
  structureNumber: string
  createdBy: string
  createdOn: string
  status: number
}

/**
 * JSON Patch operation interface
 */
export interface JsonPatchOperation {
  op: 'replace' | 'add' | 'remove' | 'copy' | 'move' | 'test'
  path: string
  value?: any
  from?: string
}

/**
 * Service for managing structure operations
 *
 * Provides structure management functionality with tokenized loading states.
 * Focuses on core structure operations without complex state management.
 *
 * Key features:
 * - Structure update operations (patch)
 * - Tokenized loading states for concurrent operations
 * - Clean separation from legacy KeyingStructureService
 */
@Injectable({
  providedIn: 'root',
})
export class StructureService {
  private readonly httpClient = inject(HttpClient)
  private readonly tokenizedMessageLoadingService = inject(TokenizedMessageLoadingService)
  private readonly keyingAppEnvironmentConfig = inject(KeyingAppEnvironmentConfig)

  /**
   * Update a structure using JSON Patch operations
   *
   * @param structureId The structure ID to update
   * @param request Array of JSON Patch operations
   * @returns Observable of updated structure
   */
  patchStructure(structureId: number, request: JsonPatchOperation[]): Observable<IStructure> {
    const token = this.tokenizedMessageLoadingService.startLoading('Updating Structure', 'patch-structure', 'medium')

    const headers = new HttpHeaders().set('Content-Type', 'application/allegion.structure.update+json')
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}`

    return this.httpClient.patch<IStructure>(url, request, { headers }).pipe(
      take(1),
      finalize(() => this.tokenizedMessageLoadingService.stopLoading(token))
    )
  }
}